import { FormikProps } from "formik";
import { useEffect, useRef } from "react";
import { Form2Payload } from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import {
  buildNoteContent,
  parseNoteContent,
} from "../initial-f2f-note-content";
import { DateLabelOverrides, InitialF2fPayload } from "../initial-f2f-types";

interface UseNoteContentSyncParams {
  formik: FormikProps<Form2Payload>;
  payload: InitialF2fPayload;
  update: (patch: Partial<InitialF2fPayload>) => void;
  dateLabels?: DateLabelOverrides;
}

/** Mirrors the note into formik's `noteContent`, and loads a saved `noteContent` back into the note. */
export const useNoteContentSync = ({
  formik,
  payload,
  update,
  dateLabels,
}: UseNoteContentSyncParams): void => {
  const { noteContent } = formik.values;
  const { setFieldValue } = formik;
  // Lazy so the note is not serialised on every render.
  const lastSynced = useRef<string | null>(null);
  if (lastSynced.current === null) {
    lastSynced.current = JSON.stringify(buildNoteContent(payload, dateLabels));
  }
  const isHydrating = useRef(false);

  useEffect(() => {
    if (!noteContent) return;
    const incoming = JSON.stringify(noteContent);
    if (incoming === lastSynced.current) return;
    const fields = parseNoteContent(noteContent);
    lastSynced.current = JSON.stringify(buildNoteContent(fields, dateLabels));
    isHydrating.current = true;
    update(fields);
  }, [noteContent, update, dateLabels]);

  useEffect(() => {
    if (isHydrating.current) {
      isHydrating.current = false;
      return;
    }
    const built = buildNoteContent(payload, dateLabels);
    const serialized = JSON.stringify(built);
    if (serialized === lastSynced.current) return;
    lastSynced.current = serialized;
    setFieldValue("noteContent", built);
  }, [payload, setFieldValue, dateLabels]);
};
